import { Button, Modal, Tabs, Textarea, useToast } from '@citizen-walk/shared-ui';
import {
  DEFAULT_POST_ANALYSIS_KEY,
  formatErrorForToast,
  fromUnknownError,
  hasFormatFieldValues,
  isDefaultPostAnalysis,
  applyOutputFieldsOverride,
  type PostAnalysisOption,
  resolvePostDetailVisibility,
  type AiAnalysisBousaiResult,
  type AiAnalysisWalkerResult,
  type AiOutputFormatSchema,
  type AiVerification,
  contrastTextColor,
  type AiVerificationAnswerValue,
  type AnalysisDisplayRules,
  type PostVisibility,
  type ResolvedPostDetailVisibility,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
// docs/105 A-③ 残課題: postDetailScores (投稿詳細の動的スコアカード列)。
// scoreEvaluator は features/analysis なので formatScoreValue は index 経由で import、
// 列の評価 + レジストリ導出 (fieldPath 解析) は posts 内の postDetailScores glue に集約。
// 列名動的化レベル 3 (docs/105 / old2 移植): M3 outputFormat を解決する useOutputFormat も
// features/analysis の公開 API なので index 経由で import (posts→analysis は既存依存)。
import { formatScoreValue, useOutputFormat } from '../analysis';
import { evaluatePostDetailScores } from './postDetailScores';
// docs/105 残課題: colorRule バッジ (投稿詳細ヘッダの種別色ラベル、markerStyle と同 evaluateColorRule)。
import { useDistrictReady } from '../district';
// 投稿詳細の位置は緯度経度の数字ではなく読み取り専用の地図プレビューで示す (PostForm と同 picker を readOnly で再利用)。
import { PostLocationPicker } from '../map';
import { ColorBadge, evaluatePostColor, useDistrictColorRule } from './ColorBadge';
// 投稿者入力項目の読み取り専用 詳細表示 (visible 駆動・ColorRule 着色・全ビュー連動)。
import { PostDetailFields } from './PostDetailFields';
import { COMMENT_MAX, COUNTER_CLASS, INPUT_CLASS, LABEL_CLASS, TITLE_MAX } from './constants';
// 列名動的化レベル 3 (docs/105): 汎用 (fields[] 駆動) AI 解析結果の generic 生データ表示。
import { GenericAiResultBlock } from './GenericAiResultBlock';
// 列名動的化レベル 3 (docs/105 / old2 移植): classifications テーブルの outputFormat 駆動の動的列。
import { ClassificationsTable } from './ClassificationsTable';
import { buildClassificationColumns } from './classificationTableRenderer';
// docs/107 Wave 10 (old2 `lib/categoryFormat.ts` 踏襲): code→ラベル整形 (full / 短縮)。
import { formatCategoryLabel, formatCategoryTag } from './categoryFormat';
// Sprint BG-DataSettings 16 (GAP-U8): file 名は `PhotoView.tsx` (case-insensitive 衝突回避、export 名は `Photo`)
import { Photo } from './PhotoView';
import { PhotoPrivacyNotice } from './PhotoPrivacyNotice';
import { useAgree } from './useAgree';
import {
  resolveChipColor,
  useAiCategoryMasters,
  type AiCategoryMastersResult,
} from './useAiCategoryMasters';
import { REANALYSIS_NOTE_MAX, requestPostReanalysis } from './requestPostReanalysis';
import {
  postDetailFieldsOf,
  useAnalysisDisplayConfig,
  type PostDetailField,
  type PostDetailFieldEntry,
} from './useAnalysisDisplayConfig';
import { useGeolocation, type GeoSample } from './useGeolocation';
import { deletePost, projectPostAnalysis, updatePost, type PostMinimal } from './usePosts';
// 投稿フォームの付帯 field (postFields 駆動) を編集モードでも再編集可能にするため、
// 投稿画面と同じ renderer (PostExtraFieldsForm) + 解決/必須判定 helper を再利用する。
import { PostExtraFieldsForm } from './PostExtraFieldsForm';
import {
  collectOtherTextValues,
  findMissingRequiredPostFields,
  getRenderablePostFields,
  resolvePostFields,
} from './postFieldsHelpers';
import { usePhotoUrl } from './usePhotoUrl';
import {
  useDistrictVerificationConfig,
  useMyVerification,
  useVerificationTemplate,
  VerificationForm,
  type MyVerificationState,
  type VerificationFormPost,
} from '../verification';

export interface PostDetailModalProps {
  open: boolean;
  onClose: () => void;
  post: PostMinimal | null;
  districtId: string;
  /** 現在の認証 uid。null は未認証。 */
  currentUid: string | null;
  /**
   * R-70 方針B: 「自分」とみなす全 uid (現 uid + 端末引き継ぎ元 linkedUids)。指定時は編集/削除の
   * owner 判定にこちらを使い、引き継ぎ後も旧端末の投稿を編集できる。省略時は currentUid 単独 (後方互換)。
   * 実書込は firestore.rules の isLinkedOwner (claim linkedUids) が最終ガード。
   */
  selfUids?: string[];
  /** 削除成功時のコールバック (modal を閉じる + AppHome の再読込促し)。 */
  onDeleted?: (postId: string) => void;
  /**
   * 地区の `analysisDisplayConfigId` (M5 master 参照、Sprint X2)。
   * 未指定は default 表示順 (`postDetailFieldsOf` で fallback)。
   */
  analysisDisplayConfigId?: string | null;
  /**
   * この地区の気づき / 処方箋 (2026-09-07)。渡すと「この投稿から生まれた気づき・処方箋」
   * セクションが出る。未指定なら非表示 (投稿タブなど、一覧を持たない画面での従来表示のまま)。
   *
   * 型は features/insights に依存しないよう構造だけを要求する (posts → insights の
   * 相互 import を避けるため)。分析画面が `InsightMinimal[]` / `IdeaMinimal[]` をそのまま渡せる。
   */
  relatedInsights?: RelatedInsightRef[];
  relatedIdeas?: RelatedIdeaRef[];
  /** 気づき / 処方箋の詳細を開く (id を渡す。呼び出し側が一覧から解決してモーダルを開く)。 */
  onSelectInsight?: (insightId: string) => void;
  onSelectIdea?: (ideaId: string) => void;
}

/** 「この投稿から生まれた気づき」に必要な最小構造。 */
export interface RelatedInsightRef {
  id: string;
  title: string;
  selectedPostIds: string[];
}

/** 「この投稿から生まれた処方箋」に必要な最小構造 (気づき経由の紐付けも辿る)。 */
export interface RelatedIdeaRef {
  id: string;
  title: string;
  selectedPostIds: string[];
  insightIds: string[];
}

/**
 * 投稿 1 件から生まれた気づき / 処方箋を選ぶ (pure、2026-09-07)。
 *
 * 気づきは投稿を直接選んでいるもの。処方箋は投稿を直接選んでいるものに加え、
 * 「その投稿から生まれた気づき」を束ねているものも含める (処方箋は気づき経由で作られるため、
 * 直接の紐付けを持たないことが多い)。
 */
export function selectRelatedKnowledge(
  postId: string,
  insights: RelatedInsightRef[],
  ideas: RelatedIdeaRef[],
): { insights: RelatedInsightRef[]; ideas: RelatedIdeaRef[] } {
  const matchedInsights = insights.filter((i) => (i.selectedPostIds ?? []).includes(postId));
  const insightIds = new Set(matchedInsights.map((i) => i.id));
  const matchedIdeas = ideas.filter(
    (d) =>
      (d.selectedPostIds ?? []).includes(postId) ||
      (d.insightIds ?? []).some((iid) => insightIds.has(iid)),
  );
  return { insights: matchedInsights, ideas: matchedIdeas };
}

/**
 * AI 解析の status を 1 行 badge で表示 (Phase 5.5 minimum)。
 * 完全な ECW Renderer (description / emotions / categories) は Phase 5 後続 (docs/17 §4-8)。
 */
function AiStatusBadge({
  status,
  error,
}: {
  status: 'pending' | 'processing' | 'done' | 'failed' | 'skipped';
  error: string | null;
}) {
  const map: Record<typeof status, { label: string; cls: string; title?: string }> = {
    pending: { label: '🤖 AI 解析待ち', cls: 'bg-slate-100 text-slate-700' },
    processing: { label: '🤖 AI 解析中…', cls: 'bg-primary-50 text-primary-700' },
    done: { label: '🤖 AI 解析済', cls: 'bg-emerald-100 text-emerald-800' },
    failed: {
      label: '🤖 AI 解析失敗',
      cls: 'bg-danger-100 text-danger-700',
      title: error ?? undefined,
    },
    // 1 日の上限などで解析しなかった投稿。理由 (日本語) は error に入る (title で出す)。
    skipped: {
      label: '🤖 AI 解析なし',
      cls: 'bg-slate-100 text-slate-500',
      title: error ?? undefined,
    },
  };
  const v = map[status];
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] ${v.cls}`} title={v.title}>
      {v.label}
    </span>
  );
}

interface EcwResult {
  description: string;
  emotionText: string;
  emotions: string[];
  emotionReason: string;
  categoryCodes: string[];
  categoryReason: string;
}

/**
 * 列名動的化レベル3 (docs/105 / 2026-05-30): `aiAnalysis.generic` から ECW フラット項目
 * (感情 / 対象分類 + 各理由) を抽出する。old2 `PostDetailModal` (L607-673) 踏襲。
 *
 * - validator が camelCase 保存するため snake_case / camelCase 両方を fallback で読む。
 * - categoryCodes は配列 / カンマ区切り string の両対応。
 * - 感情・対象分類のいずれも無ければ `null` を返す (= description / classifications のみの
 *   bousai/walker generic はこの経路では扱わず、従来どおり GenericAiResultBlock に委ねる)。
 */
function ecwResultFromGeneric(
  generic: Record<string, unknown> | null | undefined,
): EcwResult | null {
  if (!generic) return null;
  const g = generic;
  const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
  const emotionText = str(g.emotionText ?? g.emotion_text);
  const emotions = Array.isArray(g.emotions)
    ? g.emotions.map((x) => String(x).trim()).filter(Boolean)
    : [];
  const emotionReason = str(g.emotionReason ?? g.emotion_reason);
  const ccRaw = g.categoryCodes ?? g.category_codes;
  const categoryCodes = Array.isArray(ccRaw)
    ? ccRaw.map((x) => String(x).trim()).filter(Boolean)
    : typeof ccRaw === 'string'
      ? ccRaw
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
      : [];
  const categoryReason = str(g.categoryReason ?? g.category_reason);
  const hasFlat =
    emotionText !== '' ||
    emotions.length > 0 ||
    emotionReason !== '' ||
    categoryCodes.length > 0 ||
    categoryReason !== '';
  if (!hasFlat) return null;
  return {
    description: str(g.description),
    emotionText,
    emotions,
    emotionReason,
    categoryCodes,
    categoryReason,
  };
}

/**
 * Tailwind 既存色 (warning / primary / slate) に当てはまらない動的色は inline style で渡す。
 * 旧 v1 chip 表示と同等の 視認性 + WCAG コントラストを優先。
 */
function Chip({ label, color, title }: { label: string; color: string; title?: string }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ backgroundColor: color, color: contrastTextColor(color) }}
      title={title}
    >
      {label}
    </span>
  );
}

function EmotionChips({
  emotions,
  resolver,
}: {
  emotions: string[];
  resolver: AiCategoryMastersResult;
}) {
  if (emotions.length === 0) return null;
  const master = resolver.byTemplateVar.get('emotionMaster');
  return (
    <div className="flex flex-wrap gap-1.5">
      {emotions.map((emotion) => {
        // emotion label (例: '喜び') → row 解決。未解決時は raw label を slate-400 で表示
        const row = resolver.byCategoryLabel.get(emotion);
        const color = row ? resolveChipColor(row, master) : '#94A3B8';
        return <Chip key={emotion} label={row?.category ?? emotion} color={color} />;
      })}
    </div>
  );
}

function CategoryChips({
  codes,
  resolver,
}: {
  codes: string[];
  resolver: AiCategoryMastersResult;
}) {
  if (codes.length === 0) return null;
  const master = resolver.byTemplateVar.get('categoryMaster');
  return (
    <div className="flex flex-wrap gap-1.5">
      {codes.map((code) => {
        // categoryCd (例: 'D-09') → row 解決。未解決時は raw code を slate-400 で表示
        const row = resolver.byCategoryCd.get(code);
        const color = row ? resolveChipColor(row, master) : '#94A3B8';
        // old2 踏襲: "D-09 眺望・景観【環境・自然】" 形式 + title 属性 (truncate 時の hover)。
        const label = formatCategoryLabel(code, row);
        return <Chip key={code} label={label} color={color} title={label} />;
      })}
    </div>
  );
}

/**
 * ECW mode の AI 解析結果を表示する full Renderer (Sprint X2、旧 `DetailPanel.tsx` L4 踏襲)。
 *
 * Sprint X2 で minimum (raw chip 表示のみ) → 本格 Renderer に拡張:
 *   - M2 連動: emotion label / categoryCd を master.rows と照合し、表示 label + 色を解決
 *   - M5 displayRules.postDetailFields 駆動: 表示順を master で制御 (`postDetailFieldsOf` fallback あり)
 *   - rawOutput debug toggle: <details> で折畳 (parsed JSON 整形表示、開発 / 解析確認用)
 *   - analyzedAt / settingsId footer 表示 (旧 v1 と同 metadata)
 *
 * 旧と v2 の差分 (細部 UX 改善):
 *   - 旧 ECW Renderer はモード別 component (DetailPanel + MatrixView + HeatmapView 等) で全画面構成
 *     → v2 PostDetailModal 内 minimum 範囲では「テキスト + chip 列 + debug raw」に narrow
 *     (本格 Renderer = MatrixView / HeatmapView 等は Sprint X3 で分析アプリに移植)
 *   - chip は warning-100 / primary-100 等の semantic class → master.colorMap 由来の動的色 (inline bg)
 *   - rawOutput は <details> 折畳で開発時のみ展開 (旧は admin debug page で別途確認)
 */
/**
 * 動的管理拡張: post から dot-path で値を解決するヘルパー。
 *
 * - `extraFields.X` → `post.extraFields?.X`
 * - `aggregateScores.Y` → `post.aiAnalysisEcw?.aggregateScores?.Y` (互換性: ecw / bousai / walker のいずれかに展開)
 * - 任意の `a.b.c` を再帰 lookup (簡素な dot path; bracket / wildcard 未サポート)
 *
 * 戻り値は原始値 (string / number / boolean) なら toString、object / array は JSON.stringify、
 * null / undefined は null を返す。
 */
export function resolveSourceValue(post: PostMinimal, sourceKey: string): string | null {
  if (!sourceKey) return null;
  const parts = sourceKey.split('.').filter((p) => p.length > 0);
  if (parts.length === 0) return null;
  // reviewer follow-up ⚠️-4: prototype pollution / property leakage を防ぐため、
  // __proto__ / constructor / prototype のいずれかが path に含まれていれば即 null。
  // (これらは多くの object に隠れて存在し、admin が誤って sourceKey に入れた場合に
  //  関数本体や Object.prototype を文字列化してしまうリスクがあるため)。
  for (const key of parts) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') return null;
  }
  // root を選ぶ: extraFields は extraFields 直下、aggregateScores は ai*.aggregateScores、それ以外は post 直下
  let current: unknown = post as unknown as Record<string, unknown>;
  const head = parts[0];
  if (head === 'aggregateScores') {
    current =
      (post.aiAnalysisEcw as unknown as Record<string, unknown> | null) ??
      (post.aiAnalysisBousai as unknown as Record<string, unknown> | null) ??
      (post.aiAnalysisWalker as unknown as Record<string, unknown> | null) ??
      null;
    // head を含めて改めて lookup
  }
  for (const key of parts) {
    if (current === null || current === undefined) return null;
    if (typeof current !== 'object') return null;
    current = (current as Record<string, unknown>)[key];
  }
  if (current === null || current === undefined) return null;
  if (typeof current === 'string') return current;
  if (typeof current === 'number' || typeof current === 'boolean') return String(current);
  try {
    return JSON.stringify(current);
  } catch {
    return null;
  }
}

/** 動的管理拡張: PostDetailFieldEntry から legacy type と ref を取り出すヘルパー。 */
function entryType(entry: PostDetailFieldEntry): PostDetailField | 'custom' {
  return typeof entry === 'string' ? entry : entry.type;
}

function entryKey(entry: PostDetailFieldEntry, idx: number): string {
  if (typeof entry === 'string') return `${entry}-${idx}`;
  return `${entry.type}:${entry.sourceKey ?? ''}:${idx}`;
}

function EcwResultBlock({
  ecw,
  fieldOrder,
  resolver,
  rawOutput,
  analyzedAt,
  settingsId,
  post,
}: {
  ecw: EcwResult;
  fieldOrder: PostDetailFieldEntry[];
  resolver: AiCategoryMastersResult;
  rawOutput: { text: string; parsed: unknown } | null;
  analyzedAt: string | null;
  settingsId: string | null;
  /** 動的管理拡張: type='custom' の sourceKey 解決に post 参照が必要。 */
  post: PostMinimal;
}) {
  function renderEntry(entry: PostDetailFieldEntry, idx: number) {
    const key = entryKey(entry, idx);
    const t = entryType(entry);
    // 動的管理拡張: type='custom' は sourceKey 駆動で post から値を resolve
    if (t === 'custom') {
      const ref = entry as Exclude<PostDetailFieldEntry, string>;
      const sourceKey = ref.sourceKey;
      if (!sourceKey) return null;
      const value = resolveSourceValue(post, sourceKey);
      if (value === null || value === '') return null;
      const label = ref.label ?? sourceKey;
      return (
        <p key={key} className="text-[11px] text-slate-500" data-source-key={sourceKey}>
          <span className="text-slate-400">{label}:</span> {value}
        </p>
      );
    }
    // legacy 6 種は object でも string でも type に基づく switch
    const refLabel = typeof entry === 'string' ? undefined : entry.label;
    switch (t) {
      case 'description':
        if (!ecw.description) return null;
        return (
          <p key={key} className="text-sm text-slate-700 leading-relaxed">
            {ecw.description}
          </p>
        );
      case 'emotions':
        if (ecw.emotions.length === 0) return null;
        return (
          <div key={key}>
            <div className="text-[11px] text-slate-500 mb-1">{refLabel ?? '感情'}</div>
            <EmotionChips emotions={ecw.emotions} resolver={resolver} />
          </div>
        );
      case 'emotionText':
        if (!ecw.emotionText) return null;
        return (
          <p key={key} className="text-sm text-slate-600 italic">
            「{ecw.emotionText}」
          </p>
        );
      case 'emotionReason':
        if (!ecw.emotionReason) return null;
        return (
          <p key={key} className="text-[11px] text-slate-500">
            {/* docs/107 Wave 10: 理由ラベル既定文言を old2 DetailPanel に合わせる (旧「感情の根拠」→「感情選定理由」)。 */}
            <span className="text-slate-400">{refLabel ?? '感情選定理由'}:</span>{' '}
            {ecw.emotionReason}
          </p>
        );
      case 'categoryCodes':
        if (ecw.categoryCodes.length === 0) return null;
        return (
          <div key={key}>
            <div className="text-[11px] text-slate-500 mb-1">{refLabel ?? '対象分類'}</div>
            <CategoryChips codes={ecw.categoryCodes} resolver={resolver} />
          </div>
        );
      case 'categoryReason':
        if (!ecw.categoryReason) return null;
        return (
          <p key={key} className="text-[11px] text-slate-500">
            <span className="text-slate-400">{refLabel ?? '分類理由'}:</span> {ecw.categoryReason}
          </p>
        );
      default:
        return null;
    }
  }

  return (
    <section className="mb-3 p-3 rounded-md bg-emerald-50/50 border border-emerald-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-emerald-900">🤖 AI 解析結果 (ECW)</h3>
        {resolver.loading && <span className="text-[10px] text-slate-400">マスター読込中…</span>}
      </div>
      <div className="space-y-2">{fieldOrder.map((f, i) => renderEntry(f, i))}</div>
      <AnalyzedAtFooter
        analyzedAt={analyzedAt}
        settingsId={settingsId}
        borderColorClass="border-emerald-100"
      />
      {rawOutput && <RawOutputDebug rawOutput={rawOutput} />}
    </section>
  );
}

/**
 * 列名動的化レベル 3 (docs/105 / old2 移植): schema があれば classifications を fields[] 駆動の
 * 動的列テーブル (ClassificationsTable) で描画し、無ければ children (従来の簡易 <ul>) に fallback する。
 *
 * - schema が null (settingsId 無し / format 未取得 / fields[] 無し) → children を返す (回帰ゼロ)。
 * - schema があっても object-array が組めない (列なし) → ClassificationsTable が null を返すため children。
 *   admin が fields[] で列を増減すれば、ここを通る詳細テーブルがそのまま追従する。
 */
function ClassificationsTableOrList({
  schema,
  classifications,
  children,
  visibility,
}: {
  schema: AiOutputFormatSchema | null;
  classifications: Record<string, unknown>[];
  children: React.ReactNode;
  /** issue #24: 分類軸/評価ラベルの列除外に使う可視性。fieldsTable (分類セット全体の表示可否) は呼び出し側で gate する。 */
  visibility?: ResolvedPostDetailVisibility;
}) {
  // 注: issue #24 の fieldsTable (テーブル状の出力 = 分類セット表示そのもの) の on/off は、呼び出し側
  // (Bousai/Walker ResultBlock) が分類セット <div> ごと gate する。ここでは schema の有無で
  // 動的列テーブル or 簡易リスト (children) の representation を選ぶだけ。
  // schema があり、かつ object-array (= 列) が組める場合のみ動的列テーブルを採用。
  // buildClassificationColumns が null を返す (object-array 無し) ときは fallback。
  if (schema && buildClassificationColumns(schema, visibility)) {
    return (
      <ClassificationsTable
        schema={schema}
        classifications={classifications}
        visibility={visibility}
      />
    );
  }
  return <>{children}</>;
}

/**
 * raw output debug 折畳 (ECW / Bousai / Walker 共通、user 第 11 弾で抽出)。
 */
function RawOutputDebug({ rawOutput }: { rawOutput: { text: string; parsed: unknown } }) {
  return (
    <details className="mt-2 text-[10px]">
      <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
        🔍 raw output (debug)
      </summary>
      <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded overflow-auto text-[10px] leading-tight max-h-[200px]">
        {(() => {
          try {
            return JSON.stringify(rawOutput.parsed, null, 2);
          } catch {
            return rawOutput.text;
          }
        })()}
      </pre>
    </details>
  );
}

/**
 * analyzedAt + settingsId footer (ECW / Bousai / Walker 共通、user 第 11 弾で抽出)。
 */
function AnalyzedAtFooter({
  analyzedAt,
  settingsId,
  borderColorClass,
}: {
  analyzedAt: string | null;
  settingsId: string | null;
  borderColorClass: string;
}) {
  const formatted = useMemo(() => {
    if (!analyzedAt) return null;
    const d = new Date(analyzedAt);
    return Number.isNaN(d.getTime()) ? analyzedAt : d.toLocaleString('ja-JP');
  }, [analyzedAt]);
  if (!formatted && !settingsId) return null;
  return (
    <p className={`mt-3 pt-2 border-t ${borderColorClass} text-[10px] text-slate-400`}>
      {formatted && <>解析日時: {formatted}</>}
      {formatted && settingsId && <span className="mx-1">·</span>}
      {settingsId && (
        <>
          設定: <code>{settingsId}</code>
        </>
      )}
    </p>
  );
}

/**
 * Bousai (防災) mode の AI 解析結果を表示する minimum Renderer (user 第 11 弾、admin 第 13 弾 + user 第 10 弾 の後追い)。
 *
 * 表示内容:
 *   - description / professional_insight / reasoning (3 段のテキスト)
 *   - hotto_score / hatto_score (それぞれ sum/max/avg を inline 表示)
 *   - classifications[] (rank 順、各 row に category_name / hzr_name / assessment + level / score / comment)
 *
 * 旧 v1 との差分:
 *   - 旧 BousaiDetail.tsx は 4 象限 matrix view + 凡例構成 → v2 minimum は text + 表 形式に narrow
 *     (matrix view 等は Sprint X3 の analysis アプリで本格化)
 *   - chip 色解決 (M3 classificationAxes 駆動) は次 sprint、現状 assessment label のみ表示
 */
function BousaiResultBlock({
  bousai,
  rawOutput,
  analyzedAt,
  settingsId,
  schema,
  visibility,
}: {
  bousai: AiAnalysisBousaiResult;
  rawOutput: { text: string; parsed: unknown } | null;
  analyzedAt: string | null;
  settingsId: string | null;
  /** 列名動的化レベル 3 (docs/105): あれば classifications を fields[] 駆動の動的列で描画。 */
  schema: AiOutputFormatSchema | null;
  /** issue #24: 分類軸/評価ラベル/集約スコア/テーブル の表示可否 (解決済)。 */
  visibility: ResolvedPostDetailVisibility;
}) {
  return (
    <section className="mb-3 p-3 rounded-md bg-amber-50/50 border border-amber-200">
      <h3 className="text-xs font-semibold text-amber-900 mb-2">🤖 AI 解析結果 (Bousai 防災)</h3>
      <div className="space-y-2">
        {bousai.description && (
          <p className="text-sm text-slate-700 leading-relaxed">{bousai.description}</p>
        )}
        {bousai.professional_insight && (
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="text-slate-400">専門家の見解:</span> {bousai.professional_insight}
          </p>
        )}
        {bousai.reasoning && (
          <p className="text-[11px] text-slate-500">
            <span className="text-slate-400">根拠:</span> {bousai.reasoning}
          </p>
        )}
        {/* issue #24: 集約スコア (aggregateScores) の表示可否。 */}
        {visibility.aggregateScores && (
          <div className="flex gap-3 text-[11px] text-slate-600">
            {/* score の null/undefined 防御 (reviewer follow-up ⚠️B): TS 上 number 必須だが
                Firestore 異常値 (undefined/NaN) で toFixed が throw するのを防ぐ。 */}
            <span>
              ハッと (sum/max/avg): <strong>{bousai.hatto_score.sum ?? 0}</strong>/
              {bousai.hatto_score.max ?? 0}/{(bousai.hatto_score.avg ?? 0).toFixed(1)}
            </span>
            <span>
              ホッと (sum/max/avg): <strong>{bousai.hotto_score.sum ?? 0}</strong>/
              {bousai.hotto_score.max ?? 0}/{(bousai.hotto_score.avg ?? 0).toFixed(1)}
            </span>
          </div>
        )}
        {/* issue #24: テーブル状の出力 (fieldsTable) が off なら分類セット表示そのものを非表示にする
            (簡易リスト fallback にもしない = 投稿の表示・非表示として機能させる)。 */}
        {visibility.fieldsTable && bousai.classifications.length > 0 && (
          <div>
            <div className="text-[11px] text-slate-500 mb-1">
              分類セット（{bousai.classifications.length}件／重要度順）
            </div>
            {/* 列名動的化レベル 3 (docs/105 / old2 移植): schema があれば fields[] 駆動の動的列テーブル、
                無ければ従来の簡易 <ul> 表示に fallback (回帰ゼロ)。 */}
            <ClassificationsTableOrList
              schema={schema}
              classifications={bousai.classifications as unknown as Record<string, unknown>[]}
              visibility={visibility}
            >
              <ul className="space-y-1">
                {bousai.classifications.map((c, idx) => (
                  <li
                    key={`${c.rank}-${c.category_code}-${idx}`}
                    className="text-[11px] text-slate-700 border-l-2 border-amber-300 pl-2"
                  >
                    <span className="font-medium">#{c.rank}</span>{' '}
                    {/* issue #24: 分類軸 (カテゴリ/ハザード) の表示可否。 */}
                    {visibility.classificationAxes && (
                      <>
                        <code className="text-[10px] text-slate-500">{c.category_code}</code>{' '}
                        {c.category_name && <>· {c.category_name}</>}
                        {c.hzr_name && (
                          <span className="text-slate-500">
                            {' '}
                            / ハザード: <strong>{c.hzr_name}</strong>
                          </span>
                        )}
                      </>
                    )}
                    {/* issue #24: 評価ラベル + 行スコアの表示可否。 */}
                    {visibility.assessment && c.assessment && (
                      <span className="ml-1 text-amber-700">
                        {' '}
                        [{c.assessment}
                        {c.assessment_level ? `·${c.assessment_level}` : ''}]
                      </span>
                    )}
                    {visibility.assessment && typeof c.score === 'number' && (
                      <span className="ml-1 text-slate-400">score: {c.score}</span>
                    )}
                    {c.comment && (
                      <div className="text-slate-600 text-[11px] mt-0.5">{c.comment}</div>
                    )}
                  </li>
                ))}
              </ul>
            </ClassificationsTableOrList>
          </div>
        )}
      </div>
      <AnalyzedAtFooter
        analyzedAt={analyzedAt}
        settingsId={settingsId}
        borderColorClass="border-amber-100"
      />
      {rawOutput && <RawOutputDebug rawOutput={rawOutput} />}
    </section>
  );
}

/**
 * Walker (ウォーカブル) mode の AI 解析結果を表示する minimum Renderer (user 第 11 弾、admin 第 13 弾 + user 第 10 弾 の後追い)。
 *
 * 表示内容:
 *   - description / professional_insight (2 段のテキスト)
 *   - iine_score / mottainai_score / walkable_score (sum/avg または値) を inline
 *   - classifications[] (rank 順、各 row に category_name / gql_name / assessment + level / score / reasoning)
 *
 * 旧 v1 との差分: BousaiResultBlock と同 (matrix view 等は Sprint X3 で本格化)。
 * 二次分類軸は walker では gql_code/gql_name (Good Quality of Life)、bousai では hzr_code/hzr_name (hazard)。
 */
function WalkerResultBlock({
  walker,
  rawOutput,
  analyzedAt,
  settingsId,
  schema,
  visibility,
}: {
  walker: AiAnalysisWalkerResult;
  rawOutput: { text: string; parsed: unknown } | null;
  analyzedAt: string | null;
  settingsId: string | null;
  /** 列名動的化レベル 3 (docs/105): あれば classifications を fields[] 駆動の動的列で描画。 */
  schema: AiOutputFormatSchema | null;
  /** issue #24: 分類軸/評価ラベル/集約スコア/テーブル の表示可否 (解決済)。 */
  visibility: ResolvedPostDetailVisibility;
}) {
  return (
    <section className="mb-3 p-3 rounded-md bg-sky-50/50 border border-sky-200">
      <h3 className="text-xs font-semibold text-sky-900 mb-2">
        🤖 AI 解析結果 (Walker ウォーカブル)
      </h3>
      <div className="space-y-2">
        {walker.description && (
          <p className="text-sm text-slate-700 leading-relaxed">{walker.description}</p>
        )}
        {walker.professional_insight && (
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="text-slate-400">専門家の見解:</span> {walker.professional_insight}
          </p>
        )}
        {/* issue #24: 集約スコア (aggregateScores) の表示可否。 */}
        {visibility.aggregateScores && (
          <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
            {/* score の null/undefined 防御 (reviewer follow-up ⚠️B、Bousai と同方針)。 */}
            <span>
              いいね (sum/avg): <strong>{walker.iine_score.sum ?? 0}</strong>/
              {(walker.iine_score.avg ?? 0).toFixed(1)}
            </span>
            <span>
              もったいない (sum/avg): <strong>{walker.mottainai_score.sum ?? 0}</strong>/
              {(walker.mottainai_score.avg ?? 0).toFixed(1)}
            </span>
            <span>
              walkable_score: <strong>{walker.walkable_score ?? 0}</strong>
            </span>
          </div>
        )}
        {/* issue #24: テーブル状の出力 (fieldsTable) が off なら分類セット表示そのものを非表示にする。 */}
        {visibility.fieldsTable && walker.classifications.length > 0 && (
          <div>
            <div className="text-[11px] text-slate-500 mb-1">
              分類セット（{walker.classifications.length}件／重要度順）
            </div>
            {/* 列名動的化レベル 3 (docs/105 / old2 移植): schema があれば fields[] 駆動の動的列テーブル、
                無ければ従来の簡易 <ul> 表示に fallback (回帰ゼロ)。 */}
            <ClassificationsTableOrList
              schema={schema}
              classifications={walker.classifications as unknown as Record<string, unknown>[]}
              visibility={visibility}
            >
              <ul className="space-y-1">
                {walker.classifications.map((c, idx) => (
                  <li
                    key={`${c.rank}-${c.category_code}-${idx}`}
                    className="text-[11px] text-slate-700 border-l-2 border-sky-300 pl-2"
                  >
                    <span className="font-medium">#{c.rank}</span>{' '}
                    {/* issue #24: 分類軸 (カテゴリ/GQL) の表示可否。 */}
                    {visibility.classificationAxes && (
                      <>
                        <code className="text-[10px] text-slate-500">{c.category_code}</code>{' '}
                        {c.category_name && <>· {c.category_name}</>}
                        {c.gql_name && (
                          <span className="text-slate-500">
                            {' '}
                            / GQL: <strong>{c.gql_name}</strong>
                          </span>
                        )}
                      </>
                    )}
                    {/* issue #24: 評価ラベル + 行スコアの表示可否。 */}
                    {visibility.assessment && c.assessment && (
                      <span className="ml-1 text-sky-700">
                        {' '}
                        [{c.assessment}
                        {c.assessment_level ? `·${c.assessment_level}` : ''}]
                      </span>
                    )}
                    {visibility.assessment && typeof c.score === 'number' && (
                      <span className="ml-1 text-slate-400">score: {c.score}</span>
                    )}
                    {c.reasoning && (
                      <div className="text-slate-600 text-[11px] mt-0.5">{c.reasoning}</div>
                    )}
                  </li>
                ))}
              </ul>
            </ClassificationsTableOrList>
          </div>
        )}
      </div>
      <AnalyzedAtFooter
        analyzedAt={analyzedAt}
        settingsId={settingsId}
        borderColorClass="border-sky-100"
      />
      {rawOutput && <RawOutputDebug rawOutput={rawOutput} />}
    </section>
  );
}

/**
 * 検証回答 (verifications subcollection) の read-only 表示 (Sprint AQ、docs/16 §10-2 P3 H10 UI6)。
 *
 * **scope**: PostDetailModal に同 post に対する自分の検証回答を read-only で表示するだけ。
 * 回答 / 編集フォームは別 route (`VerificationListPage` → `VerificationFormPage`) で実行する
 * (旧 v1 も同じ — `PostDetailModal` には「検証する」ボタン経由で別 route に飛ばす設計)。
 *
 * 表示内容:
 *   - 未ログイン: 「ログインすると検証回答を表示できます」 placeholder
 *   - loading: skeleton
 *   - 0 件 (未回答): 「まだ検証回答がありません」placeholder (AC 必須)
 *   - 1 件以上 (= 自分の回答): perPostAnswers / perClassificationAnswers / freeComment を list 形式
 *
 * 旧 v1 との差分:
 *   - 旧は modal 内で表示せず別 route に飛ばすボタンのみ → v2 は modal 内 tab で read-only inline 表示
 *     (UI6 AC 「独立 section」「初期 AI 表示」「0 件 placeholder」充足のため)
 *   - 別 route の編集フォームへの動線は本 sprint scope 外 (機能 5.UI Sprint AQ は階層整理のみ)
 */
/**
 * この投稿から生まれた気づき / 処方箋 (2026-09-07)。
 *
 * ネットワーク図やヒートマップから投稿詳細を開いたときに、そこから 3 層 (投稿 → 気づき →
 * 処方箋) を辿れるようにするためのセクション。一覧を渡されていない画面 (投稿タブ等) では
 * 何も描画しない。処方箋は「この投稿から生まれた気づき」を束ねているものも含める。
 */
function RelatedKnowledgeSection({
  postId,
  insights,
  ideas,
  onSelectInsight,
  onSelectIdea,
}: {
  postId: string;
  insights?: RelatedInsightRef[];
  ideas?: RelatedIdeaRef[];
  onSelectInsight?: (insightId: string) => void;
  onSelectIdea?: (ideaId: string) => void;
}) {
  const related = useMemo(
    () => selectRelatedKnowledge(postId, insights ?? [], ideas ?? []),
    [postId, insights, ideas],
  );
  // 一覧そのものが渡されていない画面では、セクションごと出さない (従来表示のまま)。
  if (!insights && !ideas) return null;
  if (related.insights.length === 0 && related.ideas.length === 0) return null;

  return (
    <section
      className="mb-3 p-3 rounded-md border border-slate-200 bg-slate-50"
      data-testid="post-detail-related-knowledge"
    >
      <h3 className="text-xs font-semibold text-slate-700 mb-2">
        💡 この投稿から生まれた気づき・処方箋
      </h3>
      {related.insights.length > 0 && (
        <div className="mb-2">
          <div className="text-[11px] text-slate-500 mb-1">気づき</div>
          <ul className="grid grid-cols-1 gap-1">
            {related.insights.map((i) => (
              <li key={i.id} className="min-w-0">
                <RelatedKnowledgeItem
                  label={i.title || '(無題の気づき)'}
                  icon="◆"
                  onSelect={onSelectInsight ? () => onSelectInsight(i.id) : undefined}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      {related.ideas.length > 0 && (
        <div>
          <div className="text-[11px] text-slate-500 mb-1">処方箋</div>
          <ul className="grid grid-cols-1 gap-1">
            {related.ideas.map((d) => (
              <li key={d.id} className="min-w-0">
                <RelatedKnowledgeItem
                  label={d.title || '(無題の処方箋)'}
                  icon="●"
                  onSelect={onSelectIdea ? () => onSelectIdea(d.id) : undefined}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** 気づき / 処方箋 1 件の行。詳細を開けるときだけボタンにする。 */
function RelatedKnowledgeItem({
  label,
  icon,
  onSelect,
}: {
  label: string;
  icon: string;
  onSelect?: (() => void) | undefined;
}) {
  const body = (
    <>
      <span aria-hidden className="shrink-0 text-slate-400">
        {icon}
      </span>
      <span className="min-w-0 truncate">{label}</span>
    </>
  );
  if (!onSelect) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-slate-700 px-2 py-1">{body}</span>
    );
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 text-left text-xs text-primary-700 hover:bg-primary-50"
    >
      {body}
    </button>
  );
}

function VerificationSection({
  state,
  currentUid,
}: {
  state: MyVerificationState;
  currentUid: string | null;
}) {
  if (!currentUid) {
    return (
      <p className="text-sm text-slate-500 text-center py-6" data-testid="verification-placeholder">
        ログインすると検証回答を表示できます。
      </p>
    );
  }
  if (state.status === 'loading') {
    return (
      <p
        className="text-sm text-slate-400 text-center py-6"
        role="status"
        aria-label="検証回答読込中"
      >
        検証回答を読込中…
      </p>
    );
  }
  if (!state.verification) {
    return (
      <p className="text-sm text-slate-500 text-center py-6" data-testid="verification-placeholder">
        まだ検証回答がありません。
      </p>
    );
  }
  return <VerificationAnswerView verification={state.verification} />;
}

/** AiVerificationAnswerValue を一行テキストに整形 (read-only minimum 表示用)。 */
function formatAnswerValue(v: AiVerificationAnswerValue): string {
  switch (v.type) {
    case 'three_state':
      return v.value === 'valid' ? 'はい' : v.value === 'invalid' ? 'いいえ' : 'どちらでもない';
    case 'binary':
      return v.value === 'valid' ? 'はい' : 'いいえ';
    case 'five_scale':
      return `${v.value} / 5`;
    case 'yes_no':
      return v.value ? 'はい' : 'いいえ';
    case 'comment':
      return v.value || '(空)';
    case 'custom_choice': {
      const main = v.value.join(', ');
      return v.other ? `${main}${main ? ' / ' : ''}その他: ${v.other}` : main || '(未選択)';
    }
    default:
      return '';
  }
}

function VerificationAnswerView({ verification }: { verification: AiVerification }) {
  const perPostEntries = Object.entries(verification.perPostAnswers);
  const perClassificationEntries = Object.entries(verification.perClassificationAnswers);
  const submittedAt = useMemo(() => {
    if (!verification.submittedAt) return null;
    const d = new Date(verification.submittedAt);
    return Number.isNaN(d.getTime()) ? verification.submittedAt : d.toLocaleString('ja-JP');
  }, [verification.submittedAt]);
  return (
    <section className="space-y-3" data-testid="verification-answer">
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <span
          className={`px-1.5 py-0.5 rounded ${
            verification.completed
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-amber-100 text-amber-800'
          }`}
        >
          {verification.completed ? '✓ 回答済' : '✎ 下書き'}
        </span>
        {submittedAt && <span>回答日時: {submittedAt}</span>}
      </div>
      {perPostEntries.length > 0 && (
        <div>
          <div className="text-[11px] text-slate-500 mb-1">投稿全体への回答</div>
          <ul className="space-y-1">
            {perPostEntries.map(([qid, v]) => (
              <li key={qid} className="text-[11px] text-slate-700 border-l-2 border-slate-300 pl-2">
                <code className="text-[10px] text-slate-500">{qid}</code> ·{' '}
                <span className="text-slate-700">{formatAnswerValue(v)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {perClassificationEntries.length > 0 && (
        <div>
          <div className="text-[11px] text-slate-500 mb-1">分類別の回答</div>
          <ul className="space-y-1">
            {perClassificationEntries.map(([rank, qs]) => (
              <li
                key={rank}
                className="text-[11px] text-slate-700 border-l-2 border-slate-300 pl-2"
              >
                <span className="font-medium">#{rank}</span>
                <ul className="ml-3 mt-0.5 space-y-0.5">
                  {Object.entries(qs).map(([qid, v]) => (
                    <li key={qid}>
                      <code className="text-[10px] text-slate-500">{qid}</code> ·{' '}
                      <span>{formatAnswerValue(v)}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
      {verification.freeComment && (
        <div>
          <div className="text-[11px] text-slate-500 mb-1">自由記述</div>
          <p className="text-[11px] text-slate-700 whitespace-pre-wrap border-l-2 border-slate-300 pl-2">
            {verification.freeComment}
          </p>
        </div>
      )}
      {perPostEntries.length === 0 &&
        perClassificationEntries.length === 0 &&
        !verification.freeComment && (
          <p className="text-[11px] text-slate-400">回答内容は空です。</p>
        )}
    </section>
  );
}

/**
 * docs/105 A-③: 投稿詳細 動的スコアカード列 (old2 `ScoreSection` 踏襲)。
 *
 * M5 `displayRules.postDetailScores.enabled === true` のとき、各列を scoreEvaluator
 * (evaluatePostDetailScores glue) で評価し、数値カードを最大 3 列で表示する。
 *
 * 非表示条件 (old2 と同じ、現行挙動を破壊しない):
 *   - postDetailScores 未設定 / `enabled === false` / `columns` 空 → section 自体を出さない
 *   - 全列が null (データ全欠損) → section 非表示
 *
 * 旧 v1 / old2 との差分:
 *   - old2 は `buildScoreFieldRegistry(district.mode, postFields, outputFormatMaster)` で
 *     レジストリを構築していたが、v2 modal はそれらの master を保持しないため、列の
 *     `fieldPath` を直接解析して descriptor を導出する (postDetailScores glue 参照)。
 *   - chip/色は使わず warning 系トーンの数値カード (old2 の ScoreCell と同レイアウト)。
 *
 * 型: shared-types は `ScoreDisplayConfig` / `ScoreColumnConfig` を package root から
 * 直接 export していないため、唯一の真実 `AnalysisDisplayRules` から indexed-access で導出する
 * (新規 root export を増やさず型安全を維持)。
 */
type ScoreDisplayConfig = NonNullable<AnalysisDisplayRules['postDetailScores']>;
type ScoreColumnConfig = ScoreDisplayConfig['columns'][number];

function ScoreSection({
  post,
  config,
}: {
  post: PostMinimal;
  config: ScoreDisplayConfig | undefined;
}) {
  // parse 済 (safe) の postDetailScores を memo して列評価を安定化。
  const values = useMemo(() => {
    if (!config?.enabled || config.columns.length === 0) return null;
    return evaluatePostDetailScores(config.columns, post);
  }, [config, post]);

  if (!config?.enabled || config.columns.length === 0) return null;
  if (!values) return null;
  // 全列が null (データ全欠損) なら section ごと非表示 (old2 踏襲)。
  if (values.every((v) => v === null)) return null;

  const columns = config.columns.slice(0, 3);
  const colCount = columns.length;
  const gridColsClass =
    colCount === 1 ? 'grid-cols-1' : colCount === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <section
      className="mb-3 p-3 rounded-md border border-warning-200 bg-warning-50"
      data-testid="post-detail-scores"
    >
      <h3 className="text-xs font-semibold text-warning-800 mb-2">📊 スコア</h3>
      <div className={`grid gap-2 ${gridColsClass}`}>
        {columns.map((col, i) => (
          <ScoreCell key={col.id || `col-${i}`} column={col} value={values[i] ?? null} />
        ))}
      </div>
    </section>
  );
}

function ScoreCell({ column, value }: { column: ScoreColumnConfig; value: number | null }) {
  const decimals = column.decimals ?? 1;
  const displayValue = formatScoreValue(value, decimals, column.unit);
  return (
    <div className="bg-white border border-warning-200 rounded p-2 text-center">
      <div className="text-xs font-medium text-warning-800 mb-0.5 truncate" title={column.label}>
        {column.label || '—'}
      </div>
      <div className="text-2xl font-bold text-warning-700 leading-tight">{displayValue}</div>
    </div>
  );
}

/* 投稿者入力項目の詳細表示は features/posts/PostDetailFields (visible 駆動・ColorRule 着色・
   全ビュー連動) に一本化。旧 EcwDetailSection (Happy/Healthy/scope/duration/場所 を直書きした
   ECW 固定バッジ) は V2 のハードコード排除方針に反するため撤去 (色以外の演出は issue #25)。 */

/**
 * AI 解析結果の section (Sprint AQ で抽出、PostDetailModal の view mode 用)。
 *
 * status 別表示:
 *   - 既存 status badge は modal header に残る (post 横並び metadata の一部)
 *   - 本 section は **結果本体** の表示に専念
 *   - aiAnalysisEcw / aiAnalysisBousai / aiAnalysisWalker いずれかが ある → 該当 Renderer
 *   - いずれも無い → status による placeholder (pending / processing / failed / skipped / null)
 */
function AiResultSection({
  post,
  fieldOrder,
  categoryMasters,
  outputFormatSchema,
  visibility,
  embedded = false,
  title,
}: {
  post: PostMinimal;
  /** 動的管理拡張: legacy 6 種 string + custom ref object 混在可能。 */
  fieldOrder: PostDetailFieldEntry[];
  categoryMasters: AiCategoryMastersResult;
  /**
   * 列名動的化レベル 3 (docs/105 / old2 移植): M3 outputFormat の schema。
   * classifications テーブルを fields[] 駆動の動的列で描画するために bousai / walker / generic
   * の各 ResultBlock に配る。null のとき各 block は従来の簡易 (ul / GenericTable) 表示に fallback。
   */
  outputFormatSchema: AiOutputFormatSchema | null;
  /** issue #24: AI 出力フォーマット要素 (分類軸/評価ラベル/集約スコア/テーブル) の表示可否 (解決済)。 */
  visibility: ResolvedPostDetailVisibility;
  /** docs/29: 観点のタブの中に入れる (枠を付けない・見出しに観点名)。 */
  embedded?: boolean;
  title?: string;
}) {
  // issue #33 モードドリブン化 (2026-08-31): **出力フォーマット (M3) 駆動の表示を主経路**にする。
  // 解析結果 (generic) が M3 の fields[] に定義された項目の値を持っていれば、その fields[] の
  // 並び・ラベル・分類軸マスタで表示する (GenericAiResultBlock)。これにより ECW 決め打ちの
  // 6 項目 (description / emotions / …) 以外に admin が足した項目 (例 gql_code / sub_gql /
  // gql_reason) も投稿詳細に必ず出る。
  const formatDriven = hasFormatFieldValues(post.aiAnalysisGeneric, outputFormatSchema);

  // 【廃止予定 fallback】列名動的化レベル3 (docs/105 / 2026-05-30): 出力フォーマットが未取得 /
  // fields[] に対応する値が無い旧投稿のみ、従来の ECW 決め打ちビューに落ちる。
  // old2 `PostDetailModal` (L550-552) 踏襲 — `modeResult = generic ?? ecw`。
  const genericEcw = formatDriven ? null : ecwResultFromGeneric(post.aiAnalysisGeneric);
  const ecwView: EcwResult | null =
    genericEcw ?? (formatDriven ? null : post.aiAnalysisEcw) ?? null;

  // 脱ハードコード + 重複排除 (2026-06-13、issue #33):
  // 非ECW の generic (出力フォーマット fields[] 駆動) があれば、それを唯一の「AI 解析結果」として
  // primary 表示し、ハードコードの mode 別ブロック (Bousai/Walker) は出さない (同じ内容の二重表示を解消)。
  // generic 未生成の旧投稿だけ後方互換で従来のハードコード block に fallback する。
  // ECW は generic→EcwResultBlock の既存経路 (genericEcw) を維持する。
  const hasNonEcwGeneric = Boolean(post.aiAnalysisGeneric && !genericEcw);
  const hasResult = Boolean(
    ecwView || hasNonEcwGeneric || post.aiAnalysisBousai || post.aiAnalysisWalker,
  );

  // 中身が空の generic (旧方式の `{classifications: []}` だけ等) では空セクションを出さない。
  const genericHasValue =
    !!post.aiAnalysisGeneric &&
    Object.values(post.aiAnalysisGeneric).some((v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return true;
    });

  const genericBlock =
    post.aiAnalysisGeneric && !genericEcw && genericHasValue ? (
      <GenericAiResultBlock
        generic={post.aiAnalysisGeneric}
        schema={outputFormatSchema}
        visibility={visibility}
        primary
        embedded={embedded}
        {...(title ? { title } : {})}
        resolver={categoryMasters}
        footer={
          <>
            <AnalyzedAtFooter
              analyzedAt={post.aiAnalysisAnalyzedAt}
              settingsId={post.aiAnalysisSettingsId}
              borderColorClass="border-sky-100"
            />
            {post.aiAnalysisRaw && <RawOutputDebug rawOutput={post.aiAnalysisRaw} />}
          </>
        }
      />
    ) : null;
  if (hasResult) {
    return (
      <>
        {ecwView && (
          <EcwResultBlock
            ecw={ecwView}
            fieldOrder={fieldOrder}
            resolver={categoryMasters}
            rawOutput={post.aiAnalysisRaw}
            analyzedAt={post.aiAnalysisAnalyzedAt}
            settingsId={post.aiAnalysisSettingsId}
            post={post}
          />
        )}
        {/* 【廃止予定 fallback】generic 未生成の旧投稿のみ、従来のハードコード mode 別 Renderer に
            fallback (モードドリブン化 MD-4、2026-07-24)。非ECW generic がある現行投稿は上の
            GenericAiResultBlock (primary) が唯一の表示になる。新規地区の投稿は必ず generic を
            持つためここに到達しない。撤去条件: generic 無し旧投稿の再解析完了 or 表示不要の判断。 */}
        {!hasNonEcwGeneric && post.aiAnalysisBousai && (
          <BousaiResultBlock
            bousai={post.aiAnalysisBousai}
            rawOutput={post.aiAnalysisRaw}
            analyzedAt={post.aiAnalysisAnalyzedAt}
            settingsId={post.aiAnalysisSettingsId}
            schema={outputFormatSchema}
            visibility={visibility}
          />
        )}
        {!hasNonEcwGeneric && post.aiAnalysisWalker && (
          <WalkerResultBlock
            walker={post.aiAnalysisWalker}
            rawOutput={post.aiAnalysisRaw}
            analyzedAt={post.aiAnalysisAnalyzedAt}
            settingsId={post.aiAnalysisSettingsId}
            schema={outputFormatSchema}
            visibility={visibility}
          />
        )}
        {genericBlock}
      </>
    );
  }
  // 結果不在時は status に応じた placeholder (badge は header 側に残るので本文は補足のみ)
  switch (post.aiAnalysisStatus) {
    case 'pending':
      return (
        <p className="text-sm text-slate-500 text-center py-6" data-testid="ai-placeholder">
          AI 解析の実行待ちです。
        </p>
      );
    case 'processing':
      return (
        <p
          className="text-sm text-slate-500 text-center py-6"
          role="status"
          aria-label="AI 解析中"
          data-testid="ai-placeholder"
        >
          AI 解析を実行中です…
        </p>
      );
    case 'failed':
      return (
        <p className="text-sm text-danger-700 text-center py-6" data-testid="ai-placeholder">
          AI 解析に失敗しました
          {post.aiAnalysisError ? `: ${post.aiAnalysisError}` : ''}
        </p>
      );
    case 'skipped':
      return (
        <p className="text-sm text-slate-500 text-center py-6" data-testid="ai-placeholder">
          {/* 1 日の上限で見送った場合などは、Functions が日本語の理由を error.message に入れる */}
          {post.aiAnalysisError ?? 'AI 解析は行われませんでした。'}
        </p>
      );
    default:
      return (
        <p className="text-sm text-slate-500 text-center py-6" data-testid="ai-placeholder">
          AI 解析結果はまだありません。
        </p>
      );
  }
}

/**
 * docs/29: 追加の AI 解析の観点 1 つ分の表示。結果の解析設定 (未解析なら観点の解析設定) から
 * 出力フォーマット・カテゴリマスタを読み、既定の観点と同じ AiResultSection で描く。
 */
function AdditionalAnalysisSection({
  post,
  option,
  embedded,
  title,
  fieldOrder,
  displayOverrides,
}: {
  /** projectPostAnalysis で観点の結果を aiAnalysis* に写した投稿。 */
  post: PostMinimal;
  /** 観点 (解析設定と、表示に要る地区の上書き)。 */
  option: PostAnalysisOption;
  embedded?: boolean;
  title?: string;
  fieldOrder: PostDetailFieldEntry[];
  displayOverrides: Parameters<typeof resolvePostDetailVisibility>[1];
}) {
  const settingsId = post.aiAnalysisSettingsId ?? option.settingsId;
  const categoryMasters = useAiCategoryMasters(settingsId);
  const { format } = useOutputFormat(settingsId, option.outputFormatIdOverride ?? null);
  const schema = useMemo(
    () => applyOutputFieldsOverride(format?.schema ?? null, option.outputFieldsOverride ?? null),
    [format, option.outputFieldsOverride],
  );
  const visibility = useMemo(
    () => resolvePostDetailVisibility(schema?.postDetailVisibility, displayOverrides),
    [schema, displayOverrides],
  );
  return (
    <AiResultSection
      post={post}
      fieldOrder={fieldOrder}
      categoryMasters={categoryMasters}
      outputFormatSchema={schema}
      visibility={visibility}
      embedded={embedded}
      title={title}
    />
  );
}

/**
 * 投稿詳細モーダル (Phase 5、Modal 8 件目 consumer)。
 *
 * 旧 `PostDetailModal.tsx` を踏襲しつつ Phase 2 minimum スコープで再構成:
 *   - 写真 (original.jpg) を usePhotoUrl で取得 + 拡大表示
 *   - 同感 (agree) ボタン → useAgree (firestore.rules:70 + onAgreeWrite trigger 集計、楽観 update)
 *   - 削除ボタン (自分の post のみ) → deletePost helper (Firestore + Storage atomic)
 *   - **編集ボタン (本人のみ): title / comment / visibility を inline 編集** (Phase 5 後半)
 *     - 編集対象外: location (再取得 UI 複雑、別 commit)、photo (差し替え別 commit)
 *
 * shared-ui Modal consumer 8 件目。
 *
 * **UI3 AC 準拠 (docs/16 §10-2 P2 H3、Sprint AN)** —
 *   編集モードは **明示 Save button pattern** を採用。title / comment / visibility / photo / location は
 *   全て local draft state で保持し、「保存」 button 押下時のみ updatePost を呼ぶ。
 *   「キャンセル」 button / × button で draft 破棄、サーバー値は無変化。
 *   「👍 そう思う」 (agree toggle) は draft ではない independent action (1-tap optimistic write) なので
 *   AC の Save/即時反映 二者択一とは別 category (旧 v1 でも post 一覧の agree は 1-tap、UX 連続性維持)。
 *   削除は `window.confirm` で確認ダイアログ (将来 shared-ui ConfirmDialog に統一余地あり、UI4 範囲外)。
 */
export function PostDetailModal({
  open,
  onClose,
  post,
  districtId,
  currentUid,
  selfUids,
  onDeleted,
  analysisDisplayConfigId,
  relatedInsights,
  relatedIdeas,
  onSelectInsight,
  onSelectIdea,
}: PostDetailModalProps) {
  // hooks は post=null でも呼ぶ必要があるので defensive 引数。
  // Sprint AL で usePhotoUrl が discriminated union 化されたので status を参照する。
  // docs/106: doc 保存済 URL (post.photoUrl) を渡して cache miss 時の楽観表示 + オフライン fallback。
  const photo = usePhotoUrl(post?.photoPath ?? null, post?.photoUrl ?? null);
  const photoUrl = photo.status === 'ready' ? photo.url : undefined;
  const photoLoading = photo.status === 'loading';
  // Sprint BG-DataSettings 16 (GAP-U8): 原寸失敗時に thumb fallback できるよう thumb URL も取得。
  // docs/106: thumb も doc 保存済 URL を渡す (trigger で失効しうるが usePhotoUrl が online で自己修復)。
  const photoThumb = usePhotoUrl(post?.photoThumbPath ?? null, post?.photoThumbUrl ?? null);
  const photoThumbUrl = photoThumb.status === 'ready' ? photoThumb.url : undefined;
  const { state: agreeState, toggle: toggleAgree } = useAgree(
    districtId,
    post?.id ?? '__noop__',
    currentUid,
  );
  /**
   * 投稿者から管理者への AI 再解析の依頼 (docs/13 §9-2。投稿者は再解析を直接実行できない)。
   *
   * PostDetailModal は **常時マウントされた singleton** で `post` prop だけが差し替わるため、
   * 入力欄・送信中・依頼済みの状態はどの投稿のものかを id で持ち、表示中の投稿と一致するときだけ使う。
   *   - reanalysisForm: 理由の入力欄を開いている投稿と入力中の理由
   *   - requestingPostId: 依頼の送信中の投稿
   *   - requestedPostIds: この画面で依頼した投稿 (投稿の更新が届くまでの間も「依頼済み」にする)
   */
  const [reanalysisForm, setReanalysisForm] = useState<{ postId: string; note: string } | null>(
    null,
  );
  const [requestingPostId, setRequestingPostId] = useState<string | null>(null);
  const [requestedPostIds, setRequestedPostIds] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * docs/29: 表示中の AI 解析の観点。どの投稿で選んだかも持ち、別の投稿を開いたら既定の観点に戻す
   * (singleton modal で post prop だけが差し替わるため)。
   */
  const [analysisSelection, setAnalysisSelection] = useState<{
    postId: string | null;
    key: string;
  }>({ postId: null, key: DEFAULT_POST_ANALYSIS_KEY });
  const analysisKey =
    analysisSelection.postId === (post?.id ?? null)
      ? analysisSelection.key
      : DEFAULT_POST_ANALYSIS_KEY;
  const setAnalysisKey = (key: string) => setAnalysisSelection({ postId: post?.id ?? null, key });
  const { toast } = useToast();

  // Sprint X2: AI 解析結果 Renderer 用 M2 / M5 master を解決。
  // post 不在時は settingsId=null で hook が no-op (EMPTY_MAP_RESULT を返す)。
  const aiSettingsId = post?.aiAnalysisSettingsId ?? null;
  const categoryMasters = useAiCategoryMasters(aiSettingsId);
  // 列名動的化レベル 3 (docs/105 / old2 移植): M3 outputFormat を解決し、classifications テーブルの
  // 動的列 (列名 = itemFields[].label、code+name 集約、assessment 配色) を駆動する。
  // 未取得時 (settingsId 無し / fetch 失敗 / fields[] 無し) は各 ResultBlock が従来の簡易表示に fallback。
  // docs/29 §5-1: 既定の観点の地区の上書き (出力フォーマットの差し替え・この地区だけの出力項目) を表示にも反映する。
  const districtForAi = useDistrictReady();
  const defaultAnalysisOption = districtForAi.aiPostAnalyses?.find((o) => o.isDefault) ?? null;
  const { format: outputFormat } = useOutputFormat(
    aiSettingsId,
    defaultAnalysisOption?.outputFormatIdOverride ?? null,
  );
  const outputFormatSchema = useMemo(
    () =>
      applyOutputFieldsOverride(
        outputFormat?.schema ?? null,
        defaultAnalysisOption?.outputFieldsOverride ?? null,
      ),
    [outputFormat, defaultAnalysisOption],
  );
  const { config: displayConfig, rules: displayRules } = useAnalysisDisplayConfig(
    analysisDisplayConfigId ?? null,
  );
  const fieldOrder = useMemo(() => postDetailFieldsOf(displayConfig), [displayConfig]);
  // docs/105 A-③: 投稿詳細 動的スコアカード列の設定 (safe-parse 済、未設定なら undefined)。
  const postDetailScores = displayRules.postDetailScores;

  // docs/105: 地区 colorRule (kind 形式) を解決。null なら種別バッジは出さない。
  const colorRule = useDistrictColorRule();
  // Task C: 地区別 投稿写真設定 (写真差し替え時の圧縮/無圧縮/受付上限)。未設定なら既定で従来動作。
  const district = districtForAi;
  const photoConfig = district.photoConfig;
  // issue #24: AI 出力フォーマット要素の投稿詳細表示可否を解決する。
  // テンプレ既定 (M3 outputFormat.schema.postDetailVisibility) ← 地区上書き (district.aiOutputDisplayOverrides)。
  // 未設定キーは「表示」(後方互換)。AiResultSection 配下の各 ResultBlock / テーブルに渡して gate する。
  const aiVisibility = useMemo(
    () =>
      resolvePostDetailVisibility(
        outputFormatSchema?.postDetailVisibility,
        district.aiOutputDisplayOverrides,
      ),
    [outputFormatSchema, district.aiOutputDisplayOverrides],
  );
  // 投稿フォームの付帯 field (postFields)。投稿画面 (PostForm/PostPanel) と同じ resolvePostFields で解決し、
  // 編集モードで再編集可能にする。renderable (組込/photo/location/hidden を除いた動的 field) のみ編集対象。
  const editPostFields = useMemo(
    () => resolvePostFields(district),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataSettings/mode が postFields を決める
    [district.dataSettings, district.mode],
  );
  const renderableEditFields = useMemo(
    () => getRenderablePostFields(editPostFields),
    [editPostFields],
  );
  // post=null のときは null (hook 後の早期 return で render されない)。
  const colorInfo = post ? evaluatePostColor(post, colorRule) : null;

  // Sprint AQ (UI6): 自分の検証回答を read-only 表示するため subscribe。
  // post=null 時は postId=null で hook 内 no-op (loading 状態のまま) になる。
  // R-70 P3b: 端末引き継ぎ元 UID で検証済みなら、その回答を read-only 表示する (二重回答防止)。
  const verificationState = useMyVerification(districtId, post?.id ?? null, currentUid, selfUids);

  // 編集モード state
  const [isEditing, setIsEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [visibilityDraft, setVisibilityDraft] = useState<PostVisibility>('public');
  // 付帯 field の編集 draft。post.extraFields の全 copy で初期化し (AI 派生キー等も保持)、
  // PostExtraFieldsForm が renderable field のみ書換える。保存時は renderable key だけを dot-path 更新する。
  const [extraValuesDraft, setExtraValuesDraft] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  // 必須 (mode==='required') の付帯 field 未入力 label 一覧。投稿画面と同じ findMissingRequiredPostFields で判定。
  const missingExtra = useMemo(
    () => findMissingRequiredPostFields(editPostFields, extraValuesDraft),
    [editPostFields, extraValuesDraft],
  );

  // 写真 draft (undefined=既存維持、null=削除、File=差し替え) + プレビュー URL
  const [photoDraft, setPhotoDraft] = useState<File | null | undefined>(undefined);
  const [photoDraftPreview, setPhotoDraftPreview] = useState<string | null>(null);

  function handlePhotoDraftChange(file: File | null | undefined) {
    setPhotoDraft(file);
    if (photoDraftPreview) URL.revokeObjectURL(photoDraftPreview);
    setPhotoDraftPreview(file instanceof File ? URL.createObjectURL(file) : null);
  }

  // location draft (undefined=既存維持、GeoSample=新規取得) + 取得用 hook
  const editGeo = useGeolocation();
  const [locationDraft, setLocationDraft] = useState<GeoSample | undefined>(undefined);

  // editGeo.state.ready で locationDraft に反映 (reviewer audit ⚠️ D: state.status + sample を
  // 個別 dep で narrow、ただし current 実装は state machine が新オブジェクトを status 変化時のみ
  // 生成するため [editGeo.state] でも実害なし。明示的に narrow しておく方が意図が明確)。
  const geoStatus = editGeo.state.status;
  const geoSample = editGeo.state.sample;
  useEffect(() => {
    if (geoStatus === 'ready' && geoSample) {
      setLocationDraft(geoSample);
    }
  }, [geoStatus, geoSample]);

  // unmount / 編集終了時に Object URL を revoke (メモリリーク防止)
  useEffect(() => {
    return () => {
      if (photoDraftPreview) URL.revokeObjectURL(photoDraftPreview);
    };
  }, [photoDraftPreview]);

  // post 変化 or modal 開閉で編集モードをリセット (reviewer audit ⚠️ B 反映)。
  // 編集中 (isEditing=true) は draft 同期をスキップして編集内容を保護。
  // 同一 post の onSnapshot 再受信 (別端末更新等) で編集中 draft が上書きされるのを防ぐ。
  useEffect(() => {
    if (!open || !post) {
      setIsEditing(false);
      handlePhotoDraftChange(undefined);
      setLocationDraft(undefined);
      editGeo.reset();
      return;
    }
    if (isEditing) return;
    setTitleDraft(post.title);
    setCommentDraft(post.comment ?? '');
    setVisibilityDraft(post.visibility);
    // 付帯 field の全 copy で draft 初期化 (renderable 以外の AI 派生キー等も保持し、保存時に温存する)。
    setExtraValuesDraft({ ...(post.extraFields ?? {}) });
    handlePhotoDraftChange(undefined);
    setLocationDraft(undefined);
    editGeo.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlePhotoDraftChange / editGeo は stable
  }, [open, post, isEditing]);

  /**
   * 自由検証 (2026-09-05、オーナー要望)。
   *
   * これまで投稿詳細には自分の回答の read-only 表示しか無く、回答するにはドロワーの
   * 「AI 判定をチェック」を開き直す必要があった (旧 v1 にあった「検証する」ボタンが v2 に未移植)。
   * 「そう思う」と並べてボタンを置き、詳細内でそのまま回答できるようにする。
   *
   * 出す条件は地区が自由検証を許可し (allowFreeVerification + activeTemplateId)、その投稿の
   * AI 解析が終わっていること。配分セット経由の回答は従来どおり「AI 判定をチェック」から。
   */
  const verificationConfig = useDistrictVerificationConfig(districtId);
  const activeTemplateId =
    verificationConfig.status === 'ready' ? verificationConfig.config.activeTemplateId : null;
  const freeVerificationAllowed =
    verificationConfig.status === 'ready' &&
    verificationConfig.config.allowFreeVerification &&
    activeTemplateId !== null;
  const verificationTemplate = useVerificationTemplate(
    districtId,
    freeVerificationAllowed ? activeTemplateId : null,
  );
  const [verifying, setVerifying] = useState(false);
  // 別の投稿に切り替えたり閉じたりしたら回答フォームは畳む。
  useEffect(() => {
    setVerifying(false);
  }, [post?.id, open]);

  if (!post) return null;

  // 自由検証のボタンを出すか (テンプレ取得済 + その投稿の AI 解析が完了)。
  const canVerifyHere =
    freeVerificationAllowed &&
    verificationTemplate.status === 'ready' &&
    post.aiAnalysisStatus === 'done';

  /** 検証フォームに渡す投稿 (VerificationListPage と同じ形)。 */
  const verificationFormPost: VerificationFormPost = {
    id: post.id,
    title: post.title,
    authorUid: post.authorUid || null,
    comment: post.comment,
    aiAnalysisAnalyzedAt: post.aiAnalysisAnalyzedAt,
    aiAnalysisSettingsId: post.aiAnalysisSettingsId,
    aiAnalysisEcw: post.aiAnalysisEcw,
    aiAnalysisBousai: post.aiAnalysisBousai,
    aiAnalysisWalker: post.aiAnalysisWalker,
    aiAnalysisGeneric: post.aiAnalysisGeneric,
    photoPath: post.photoPath,
    photoThumbPath: post.photoThumbPath,
    photoUrl: post.photoUrl,
    photoThumbUrl: post.photoThumbUrl,
    location: post.location
      ? { latitude: post.location.latitude, longitude: post.location.longitude }
      : null,
  };

  // R-70: 現 uid + 引き継ぎ元 (selfUids) のいずれかが authorUid と一致すれば本人 (編集/削除可)。
  // selfUids 未指定は currentUid 単独 (後方互換)。最終的な書込可否は firestore.rules が判定。
  const ownUids = selfUids && selfUids.length > 0 ? selfUids : currentUid ? [currentUid] : [];
  const isOwner = ownUids.includes(post.authorUid);
  // 再解析の依頼: 投稿に依頼が付いている (管理者が再解析すると消える) か、この画面で依頼した直後。
  const reanalysisRequested = !!post.reanalysisRequested || requestedPostIds.has(post.id);
  const isRequestingReanalysis = requestingPostId === post.id;
  const reanalysisFormOpen = reanalysisForm?.postId === post.id && !reanalysisRequested;
  const formattedCreatedAt = post.createdAt?.toDate().toLocaleString('ja-JP') ?? '';

  const titleTrimmed = titleDraft.trim();
  const canSave =
    !saving &&
    titleTrimmed.length > 0 &&
    titleTrimmed.length <= TITLE_MAX &&
    commentDraft.length <= COMMENT_MAX &&
    // 投稿画面と同じく必須の付帯 field が未入力なら保存不可。
    missingExtra.length === 0;

  async function handleDelete() {
    if (!post) return;
    if (!confirm(`「${post.title}」を削除しますか? この操作は取り消せません。`)) return;
    try {
      await deletePost(districtId, post.id, post.photoPath, post.photoThumbPath);
      toast('success', '投稿を削除しました');
      onDeleted?.(post.id);
      onClose();
    } catch (e) {
      // Sprint BF Part 5: AppError 経由で sub-class 別 prefix 付き toast。
      toast('error', `削除失敗: ${formatErrorForToast(fromUnknownError(e))}`);
    }
  }

  async function handleAgreeToggle() {
    try {
      await toggleAgree();
    } catch (e) {
      toast('error', `同感操作失敗: ${formatErrorForToast(fromUnknownError(e))}`);
    }
  }

  async function handleRequestReanalysis() {
    if (!post || requestingPostId) return;
    const target = { id: post.id, title: post.title };
    const note = reanalysisForm?.postId === post.id ? reanalysisForm.note : '';
    setRequestingPostId(target.id);
    try {
      // docs/29: 選んでいる観点の再解析を依頼する (既定の観点は null)。
      const status = await requestPostReanalysis({
        districtId,
        postId: target.id,
        surveyKey: isDefaultPostAnalysis(analysisKey) ? null : analysisKey,
        note,
      });
      toast(
        status === 'already-requested' ? 'info' : 'success',
        status === 'already-requested' ? 'すでに依頼しています' : '管理者に再解析を依頼しました',
      );
      setRequestedPostIds((prev) => new Set(prev).add(target.id));
      setReanalysisForm((f) => (f?.postId === target.id ? null : f));
    } catch (e) {
      toast('error', `再解析の依頼に失敗しました: ${formatErrorForToast(fromUnknownError(e))}`);
    } finally {
      setRequestingPostId((id) => (id === target.id ? null : id));
    }
  }

  async function handleSave() {
    if (!post || !canSave) return;
    setSaving(true);
    try {
      await updatePost(districtId, post.id, {
        title: titleTrimmed,
        comment: commentDraft.trim() === '' ? null : commentDraft.trim(),
        visibility: visibilityDraft,
        // photoDraft が undefined のときは photo フィールド変更なし
        ...(photoDraft !== undefined ? { photo: photoDraft } : {}),
        // Task C: 写真差し替え時に地区別 photoConfig (圧縮/無圧縮/受付上限) を適用。
        photoConfig,
        existingPhotoPath: post.photoPath,
        existingPhotoThumbPath: post.photoThumbPath,
        // locationDraft があるときのみ location/accuracy/source 更新
        ...(locationDraft
          ? {
              location: locationDraft.location,
              accuracy: locationDraft.accuracy,
              source: 'gps' as const,
            }
          : {}),
        // 付帯 field: renderable な field の key→値 のみを渡す (AI 派生キー等は draft に保持されているが
        // updatePost が dot-path で個別更新するため未送信 = 温存)。空値は null でクリアを表現。
        ...(renderableEditFields.length > 0
          ? {
              extraFields: {
                ...Object.fromEntries(
                  renderableEditFields.map((f) => [f.key, extraValuesDraft[f.key] ?? null]),
                ),
                // 選択肢「その他」の記入内容。外した / 消したときは null で消す。
                ...collectOtherTextValues(renderableEditFields, extraValuesDraft),
              },
            }
          : {}),
      });
      toast('success', '投稿を更新しました');
      setIsEditing(false);
      handlePhotoDraftChange(undefined);
      setLocationDraft(undefined);
      editGeo.reset();
      // onSnapshot で post list が再読込される (PostMinimal は parent から渡される)
    } catch (e) {
      toast('error', `更新失敗: ${formatErrorForToast(fromUnknownError(e))}`);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    if (!post) return;
    setTitleDraft(post.title);
    setCommentDraft(post.comment ?? '');
    setVisibilityDraft(post.visibility);
    setExtraValuesDraft({ ...(post.extraFields ?? {}) });
    handlePhotoDraftChange(undefined);
    setLocationDraft(undefined);
    editGeo.reset();
    setIsEditing(false);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? `編集: ${titleDraft || post.title}` : post.title}
      size="lg"
    >
      {/* B (2026-06-09): titleGeneration による AI サブタイトル。ユーザー投稿タイトル (Modal header) の
          直下に少し目立つ形で表示する。編集中は出さない。 */}
      {!isEditing && post.aiAnalysisGeneratedTitle && (
        <p className="mb-2 flex items-start gap-1.5 text-sm font-semibold leading-snug text-sky-800">
          <span className="mt-0.5 shrink-0 rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-700">
            AI
          </span>
          <span>{post.aiAnalysisGeneratedTitle}</span>
        </p>
      )}
      <div className="text-xs text-slate-500 mb-2 flex flex-wrap items-center gap-1.5">
        <span>
          by {post.authorNickname} · {formattedCreatedAt}
        </span>
        {post.visibility === 'hidden_others' && (
          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px]">
            🔒 自分のみ
          </span>
        )}
        {post.aiAnalysisStatus && (
          <AiStatusBadge status={post.aiAnalysisStatus} error={post.aiAnalysisError} />
        )}
        {/* docs/105: 地区 colorRule 由来の種別バッジ (色 + ラベル + (あれば) アイコン)。
            colorRule 未設定 or 評価ラベル空なら ColorBadge 内で非描画。 */}
        {colorInfo && <ColorBadge evaluated={colorInfo} variant="pill" />}
      </div>

      {(photoUrl || photoThumbUrl) && (
        // Sprint BG-DataSettings 16 (GAP-U8): inline <img> → Photo component に置換。
        // 原寸 (photoUrl) 失敗時は thumb (photoThumbUrl) に fallback、両方失敗で placeholder。
        // クリックで全画面 Lightbox (旧 v1 と同 UX)。機内モード時は最初から thumb 優先。
        <div className="mb-3">
          <Photo
            src={photoUrl ?? null}
            thumbSrc={photoThumbUrl ?? null}
            alt={`${post.title} の写真`}
            className="w-full max-h-[60vh] object-contain rounded-lg border border-slate-200 bg-slate-50"
            projection={post.photo?.projection ?? null}
            baseHeadingDeg={post.photo?.poseHeadingDeg ?? null}
          />
        </div>
      )}
      {/* docs/28 §4-9: ぼかし処理の状態 (ほかの参加者には「写真は準備中です」、本人には注意書き)。 */}
      <PhotoPrivacyNotice
        status={post.photoPrivacyStatus}
        finalFailure={post.photoPrivacyFinalFailure ?? false}
        withheld={post.photoWithheld ?? false}
      />
      {post.photoPath && !photoUrl && photoLoading && (
        <p className="text-sm text-slate-500 text-center py-8">写真を読込中…</p>
      )}
      {post.photoPath && !photoUrl && photo.status === 'failed' && (
        <p className="text-sm text-slate-500 text-center py-8">
          写真の読込に失敗しました
          {photo.attempt < 3 && (
            <>
              {' '}
              <button
                type="button"
                onClick={photo.retry}
                className="ml-1 px-2 py-0.5 rounded border border-slate-400 bg-white text-slate-700 text-xs hover:bg-slate-50"
              >
                再試行
              </button>
            </>
          )}
        </p>
      )}

      {isEditing ? (
        <div className="space-y-3 mb-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="edit-title">
              タイトル <span className="text-danger-600">*</span>
            </label>
            <input
              id="edit-title"
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              maxLength={TITLE_MAX}
              className={INPUT_CLASS}
            />
            <small className={COUNTER_CLASS}>
              {titleTrimmed.length} / {TITLE_MAX}
            </small>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="edit-comment">
              コメント
            </label>
            <textarea
              id="edit-comment"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              maxLength={COMMENT_MAX}
              rows={4}
              className={`${INPUT_CLASS} resize-y`}
            />
            <small className={COUNTER_CLASS}>
              {commentDraft.length} / {COMMENT_MAX}
            </small>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="edit-visibility">
              公開範囲
            </label>
            <select
              id="edit-visibility"
              value={visibilityDraft}
              onChange={(e) => setVisibilityDraft(e.target.value as PostVisibility)}
              className={INPUT_CLASS}
            >
              <option value="public">公開 (全員が見る)</option>
              <option value="hidden_others">自分のみ (下書き / メモ用途)</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="edit-photo">
              写真
              {photoDraft === undefined && post.photoPath && (
                <span className="ml-2 text-[10px] text-slate-500">(現在: 既存写真あり)</span>
              )}
              {photoDraft === null && (
                <span className="ml-2 text-[10px] text-danger-600">(削除します)</span>
              )}
              {photoDraft instanceof File && (
                <span className="ml-2 text-[10px] text-primary-700">(差し替え予定)</span>
              )}
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                id="edit-photo"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handlePhotoDraftChange(e.target.files?.[0] ?? undefined)}
                className="text-xs"
              />
              {post.photoPath && photoDraft === undefined && (
                <button
                  type="button"
                  onClick={() => handlePhotoDraftChange(null)}
                  aria-label="既存の写真を削除"
                  className="text-xs text-danger-600 hover:text-danger-700 underline bg-transparent border-none p-0 cursor-pointer"
                >
                  写真を削除
                </button>
              )}
              {photoDraft !== undefined && (
                <button
                  type="button"
                  onClick={() => handlePhotoDraftChange(undefined)}
                  aria-label="写真の変更を取消"
                  className="text-xs text-slate-600 hover:text-slate-900 underline bg-transparent border-none p-0 cursor-pointer"
                >
                  写真の変更を取消
                </button>
              )}
            </div>
            {photoDraftPreview && (
              <img
                src={photoDraftPreview}
                alt="新しい写真プレビュー"
                className="mt-2 max-w-full max-h-[200px] rounded-md border border-slate-300"
              />
            )}
          </div>
          <div>
            <label className={LABEL_CLASS}>
              位置情報
              {locationDraft && (
                <span className="ml-2 text-[10px] text-primary-700">(再取得済)</span>
              )}
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={editGeo.capture}
                disabled={editGeo.state.status === 'pending'}
                className="px-3 py-1.5 rounded-md text-sm border min-h-touch focus:outline-none focus:ring-2 focus:ring-primary-400 bg-primary-600 text-white border-transparent hover:bg-primary-700 disabled:opacity-50 disabled:cursor-wait"
              >
                {editGeo.state.status === 'pending' ? '取得中...' : '📍 現在地を再取得'}
              </button>
              {locationDraft && (
                <>
                  <span className="text-xs text-slate-600">
                    {locationDraft.location.latitude.toFixed(5)},{' '}
                    {locationDraft.location.longitude.toFixed(5)}{' '}
                    <span className="text-slate-400">
                      (±{Math.round(locationDraft.accuracy)} m)
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setLocationDraft(undefined);
                      editGeo.reset();
                    }}
                    aria-label="位置情報の変更を取消"
                    className="text-xs text-slate-600 hover:text-slate-900 underline bg-transparent border-none p-0 cursor-pointer"
                  >
                    取消
                  </button>
                </>
              )}
              {editGeo.state.status === 'error' && (
                <span className="text-xs text-danger-600">
                  取得失敗: {editGeo.state.error.message}
                </span>
              )}
            </div>
            {!locationDraft && post.location && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                現在: {post.location.latitude.toFixed(5)}, {post.location.longitude.toFixed(5)}
                {post.accuracy != null && ` (±${Math.round(post.accuracy)}m)`}
              </p>
            )}
          </div>
          {/* 投稿フォームの付帯 field (postFields 駆動) を投稿画面と同じ widget で再編集。
              renderable な field が無い地区 (mode template / dataSettings に動的 field が無い) では非表示。 */}
          {renderableEditFields.length > 0 && (
            <div>
              <label className={LABEL_CLASS}>追加の項目</label>
              <PostExtraFieldsForm
                fields={editPostFields}
                values={extraValuesDraft}
                onChange={setExtraValuesDraft}
                disabled={saving}
              />
              {missingExtra.length > 0 && (
                <p className="text-xs text-danger-700 mt-1">
                  未入力の必須項目: {missingExtra.join('、')}
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {post.comment && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{post.comment}</p>
          )}
          {post.location && (
            <div className="mb-3">
              {/* 緯度経度の数字ではなく読み取り専用の地図で投稿位置を示す。マーカー色は地区 ColorRule の
                  bucket 色 (一覧/地図のピンと一致)、未設定地区は picker 既定色 (#ec4899)。 */}
              <PostLocationPicker
                location={post.location}
                markerColor={colorInfo?.color ?? '#ec4899'}
                zoom={16}
                readOnly
                className="h-48"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                📍 {post.location.latitude.toFixed(5)}, {post.location.longitude.toFixed(5)}
                {post.accuracy != null && <span> (±{Math.round(post.accuracy)}m)</span>}
              </p>
            </div>
          )}
          {/* 投稿者が入力した項目 (「詳細表示」=visible の postField) を全ビュー連動で表示。
              色は地区 ColorRule の bucket 色のみに基づく (それ以外の演出は issue #25)。 */}
          <PostDetailFields
            fields={editPostFields}
            values={post.extraFields}
            colorRule={colorRule}
          />
          {/* docs/105 A-③: 動的スコアカード列 (M5 postDetailScores.enabled のときのみ、AI/検証 tab の上)。 */}
          <ScoreSection post={post} config={postDetailScores} />
          {/* Sprint AQ (UI6): AI 解析結果 と 検証回答 を独立 section に分離し tab で切替。
              初期 tab = AI (AC「初期は AI 表示」)、検証 0 件でも tab + placeholder で
              section が空にならない (AC「AI と混ざらない」)。 */}
          <Tabs
            aria-label="投稿の AI 解析と検証回答"
            defaultActive="ai"
            tabs={[
              {
                id: 'ai',
                label: '🤖 AI 解析',
                content: (() => {
                  const defaultSection = (embedded: boolean, title?: string) => (
                    <AiResultSection
                      post={post}
                      fieldOrder={fieldOrder}
                      categoryMasters={categoryMasters}
                      outputFormatSchema={outputFormatSchema}
                      visibility={aiVisibility}
                      embedded={embedded}
                      title={title}
                    />
                  );
                  // docs/29: 追加の観点がある地区だけ、観点の切り替えを出す。
                  const options = district.aiPostAnalyses;
                  if (!options || options.length < 2) return defaultSection(false);
                  return (
                    <div className="mt-1">
                      {/* フォルダ型のタブ: 選んだ観点のタブと結果の枠を一続きにし、何が切り替わるかを見せる。 */}
                      <Tabs
                        variant="folder"
                        aria-label="AI 解析の観点"
                        activeId={
                          options.some((o) => o.key === analysisKey)
                            ? analysisKey
                            : DEFAULT_POST_ANALYSIS_KEY
                        }
                        onChange={setAnalysisKey}
                        tabs={options.map((o) => ({
                          id: o.key,
                          label: o.label,
                          content: o.isDefault ? (
                            defaultSection(true, `🤖 ${o.label}の AI 解析結果`)
                          ) : (
                            <AdditionalAnalysisSection
                              post={projectPostAnalysis(post, o.key)}
                              option={o}
                              embedded
                              title={`🤖 ${o.label}の AI 解析結果`}
                              fieldOrder={fieldOrder}
                              displayOverrides={district.aiOutputDisplayOverrides}
                            />
                          ),
                        }))}
                      />
                    </div>
                  );
                })(),
              },
              {
                id: 'verification',
                label: '✓ 検証回答',
                content: <VerificationSection state={verificationState} currentUid={currentUid} />,
              },
            ]}
          />

          {/* この投稿から生まれた気づき / 処方箋 (2026-09-07)。分析画面から一覧を渡されたときだけ出る。 */}
          <RelatedKnowledgeSection
            postId={post.id}
            insights={relatedInsights}
            ideas={relatedIdeas}
            onSelectInsight={onSelectInsight}
            onSelectIdea={onSelectIdea}
          />
        </>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => void handleAgreeToggle()}
            disabled={!currentUid || agreeState.loading || isEditing}
            aria-pressed={agreeState.isAgreed ?? false}
            className={`px-3 py-1.5 rounded-lg text-sm border min-h-touch focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 ${
              agreeState.isAgreed
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {agreeState.isAgreed ? '✓ 同感しました' : '👍 そう思う'}
            <span className="ml-2 text-xs text-slate-500">({post.agreeCount})</span>
          </button>

          {/* 2026-09-05: 自由検証が許可 + AI 解析済のときだけ。詳細内でそのまま回答する。 */}
          {canVerifyHere && !isEditing && (
            <button
              type="button"
              onClick={() => setVerifying((v) => !v)}
              disabled={!currentUid}
              aria-expanded={verifying}
              data-testid="verify-here-button"
              className={`px-3 py-1.5 rounded-lg text-sm border min-h-touch focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 ${
                verifying
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {verifying
                ? '✕ 検証をとじる'
                : verificationState.verification
                  ? '🤖 検証をなおす'
                  : '🤖 AI 判定を検証'}
            </button>
          )}
        </div>

        {isOwner && !isEditing && (
          <div className="flex gap-2 flex-wrap">
            {post.photoPath && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setReanalysisForm({ postId: post.id, note: '' })}
                disabled={reanalysisRequested || reanalysisFormOpen || isRequestingReanalysis}
                aria-expanded={reanalysisFormOpen}
                data-testid="request-reanalysis-button"
              >
                {reanalysisRequested ? '再解析を依頼済み' : '🤖 再解析を依頼'}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
              編集
            </Button>
            <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
              削除
            </Button>
          </div>
        )}
      </div>

      {/* 投稿者 → 管理者への再解析の依頼 (docs/13 §9-2)。理由は任意。 */}
      {isOwner && !isEditing && reanalysisFormOpen && (
        <div
          className="mt-3 p-3 rounded-lg border border-slate-200 bg-slate-50 grid grid-cols-1 gap-2"
          data-testid="request-reanalysis-form"
        >
          <p className="text-sm text-slate-700">
            AI の解析結果が写真と合わないときは、管理者に解析のやり直しを依頼できます。
          </p>
          <Textarea
            label="理由（任意）"
            value={reanalysisForm?.note ?? ''}
            onChange={(v) => setReanalysisForm({ postId: post.id, note: v })}
            rows={2}
            maxLength={REANALYSIS_NOTE_MAX}
            disabled={isRequestingReanalysis}
            placeholder="例: 写っている建物の種類がちがう"
            hint={`${Array.from(reanalysisForm?.note ?? '').length} / ${REANALYSIS_NOTE_MAX} 文字`}
          />
          <div className="flex gap-2 justify-end flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReanalysisForm(null)}
              disabled={isRequestingReanalysis}
            >
              やめる
            </Button>
            <Button
              size="sm"
              onClick={() => void handleRequestReanalysis()}
              disabled={isRequestingReanalysis}
              data-testid="request-reanalysis-submit"
            >
              {isRequestingReanalysis ? '依頼中…' : '依頼する'}
            </Button>
          </div>
        </div>
      )}

      {/* 2026-09-05: 詳細内でそのまま検証に回答する (自由検証)。assignmentSetId=null で保存される。 */}
      {verifying && canVerifyHere && currentUid && verificationTemplate.status === 'ready' && (
        <div className="mt-4 pt-3 border-t border-slate-200" data-testid="inline-verification-form">
          <VerificationForm
            districtId={districtId}
            uid={currentUid}
            {...(selfUids && selfUids.length > 0 ? { selfUids } : {})}
            post={verificationFormPost}
            template={verificationTemplate.template}
            assignmentSetId={null}
            existingVerification={verificationState.verification}
            // 投稿内容と AI 解析結果はモーダル本体が既に出しているので、質問だけを描画する。
            showPostContext={false}
            onSubmitted={() => setVerifying(false)}
            onCancel={() => setVerifying(false)}
          />
        </div>
      )}

      {isEditing ? (
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" size="md" onClick={handleCancelEdit} disabled={saving}>
            キャンセル
          </Button>
          <Button variant="primary" size="md" onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end mt-4">
          <Button variant="secondary" size="md" onClick={onClose}>
            閉じる
          </Button>
        </div>
      )}
    </Modal>
  );
}
